import { ChevronDown } from 'lucide-react-native';
import { useId } from 'react';
import { twJoin } from 'tailwind-merge';

import {
  CancerTypeCombobox,
  type CancerTypeLookup,
} from '@/components/directory/CancerTypeCombobox';
import { ProductTypeCombobox } from '@/components/directory/ProductTypeCombobox';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { DirectorySearchBox } from '@/features/directory';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { PRODUCT_TYPES, getProductTypeById } from '@/services/getAlgoliaExperts';
import { segmentClient } from '@/util/analytics';

export type { CancerTypeLookup };

type DirectoryFiltersProps = {
  focusId: string;
  setFocusId: (value: string) => void;
  selectedCancerTypeLabel: string;
  cancerTypeLookup?: CancerTypeLookup;
  colors: { typography: Record<number, string> };
  bottom: number;
};

type DirectoryModFiltersProps = DirectoryFiltersProps & {
  productTypeId: string;
  setProductTypeId: (value: string) => void;
  selectedProductTypeLabel: string;
};

type DirectoryProductTypeFilterProps = Pick<
  DirectoryModFiltersProps,
  'productTypeId' | 'setProductTypeId' | 'selectedProductTypeLabel' | 'colors' | 'bottom'
> & {
  className?: string;
};

type DirectoryCancerTypeFilterProps = Pick<
  DirectoryFiltersProps,
  'focusId' | 'setFocusId' | 'selectedCancerTypeLabel' | 'cancerTypeLookup' | 'colors' | 'bottom'
> & {
  className?: string;
};

function trackCancerTypeSelection(cancerTypeId: string, cancerTypeLookup?: CancerTypeLookup): void {
  Promise.resolve(
    segmentClient.track(ANALYTICS_EVENTS.CANCER_SELECTED, {
      cancer_type: cancerTypeLookup?.get(Number(cancerTypeId)) || '',
    })
  ).catch(() => undefined);
}

function DirectoryCancerTypeLegacySelect({
  className,
  focusId,
  setFocusId,
  selectedCancerTypeLabel,
  cancerTypeLookup,
  colors,
  bottom,
}: DirectoryCancerTypeFilterProps) {
  const options = [...(cancerTypeLookup?.entries() ?? [])].map(([id, focus]) => ({
    value: String(id),
    label: focus.label,
  }));

  return (
    <Select
      className={className}
      onOpen={() => {
        Promise.resolve(segmentClient.track(ANALYTICS_EVENTS.CANCER_TYPE_VIEWED)).catch(
          () => undefined
        );
      }}
      onValueChange={(cancerTypeId) => {
        trackCancerTypeSelection(cancerTypeId, cancerTypeLookup);

        setFocusId(cancerTypeId);
      }}
      selectedValue={focusId || ''}>
      <SelectTrigger className="flex w-full items-center justify-between gap-2 bg-background-0 px-4 py-2">
        <SelectInput
          className="px-4 placeholder:text-typography-600"
          style={{ color: colors.typography[900] }}
          placeholder="Select cancer type"
          value={selectedCancerTypeLabel}
        />
        <SelectIcon as={ChevronDown} size="lg" className="text-typography-500" />
      </SelectTrigger>
      <SelectPortal>
        <SelectBackdrop />
        <SelectContent className={twJoin('w-full', `max-h-[70vh] pb-${bottom}`)}>
          <SelectDragIndicatorWrapper>
            <SelectDragIndicator />
          </SelectDragIndicatorWrapper>
          <SelectScrollView>
            <SelectItem key="Select All" label="Select All" value="" />
            {options.map((option) => (
              <SelectItem key={option.value} label={option.label} value={option.value} />
            ))}
          </SelectScrollView>
        </SelectContent>
      </SelectPortal>
    </Select>
  );
}

function DirectoryCancerTypeFilter(props: DirectoryCancerTypeFilterProps) {
  const isDirectoryCancerTypeComboboxEnabled = useFeatureFlag(
    FEATURE_FLAGS.DIRECTORY_CANCER_TYPE_COMBOBOX
  );

  if (isDirectoryCancerTypeComboboxEnabled) {
    return <CancerTypeCombobox {...props} />;
  }

  return <DirectoryCancerTypeLegacySelect {...props} />;
}

function DirectoryProductTypeLegacySelect({
  className,
  productTypeId,
  setProductTypeId,
  selectedProductTypeLabel,
  colors,
  bottom,
}: DirectoryProductTypeFilterProps) {
  const productTypeInputId = useId();

  return (
    <Select
      className={className}
      onOpen={() => {
        Promise.resolve(segmentClient.track(ANALYTICS_EVENTS.PRODUCT_TYPE_VIEWED)).catch(
          () => undefined
        );
      }}
      onValueChange={(productType) => {
        Promise.resolve(
          segmentClient.track(ANALYTICS_EVENTS.PRODUCT_TYPE_SELECTED, {
            product_type: getProductTypeById(productType)?.label || '',
          })
        ).catch(() => undefined);

        setProductTypeId(productType);
      }}
      selectedValue={productTypeId || ''}>
      <SelectTrigger className="flex w-full items-center justify-between gap-2 bg-background-0 px-4 py-2">
        <SelectInput
          nativeID={productTypeInputId}
          className="min-w-0 flex-1 px-4 placeholder:text-typography-600"
          style={{ color: colors.typography[900] }}
          placeholder="Select treatment type"
          value={selectedProductTypeLabel}
        />
        <SelectIcon as={ChevronDown} size="lg" className="shrink-0 text-typography-500" />
      </SelectTrigger>
      <SelectPortal>
        <SelectBackdrop />
        <SelectContent className={twJoin('w-full', `max-h-[70vh] pb-${bottom}`)}>
          <SelectDragIndicatorWrapper>
            <SelectDragIndicator />
          </SelectDragIndicatorWrapper>
          <SelectScrollView>
            <SelectItem key="Select All" label="Select All" value="" />
            {PRODUCT_TYPES.map((productType) => (
              <SelectItem key={productType.id} label={productType.label} value={productType.id} />
            ))}
          </SelectScrollView>
        </SelectContent>
      </SelectPortal>
    </Select>
  );
}

/**
 * Follows the cancer type dropdown's flag, so the two are always drawn by the same kind of
 * component: both with the shared picker, or both with the plain select it replaced.
 */
function DirectoryProductTypeFilter(props: DirectoryProductTypeFilterProps) {
  const isDirectoryCancerTypeComboboxEnabled = useFeatureFlag(
    FEATURE_FLAGS.DIRECTORY_CANCER_TYPE_COMBOBOX
  );

  if (isDirectoryCancerTypeComboboxEnabled) {
    return <ProductTypeCombobox {...props} />;
  }

  return <DirectoryProductTypeLegacySelect {...props} />;
}

export const DirectoryFilters = ({
  focusId,
  setFocusId,
  selectedCancerTypeLabel,
  cancerTypeLookup,
  colors,
  bottom,
}: DirectoryFiltersProps) => {
  return (
    <>
      <DirectorySearchBox />

      <DirectoryCancerTypeFilter
        className="w-full min-w-0 flex-none md:w-80 md:shrink-0"
        focusId={focusId}
        setFocusId={setFocusId}
        selectedCancerTypeLabel={selectedCancerTypeLabel}
        cancerTypeLookup={cancerTypeLookup}
        colors={colors}
        bottom={bottom}
      />
    </>
  );
};

export const DirectoryModFilters = ({
  focusId,
  setFocusId,
  selectedCancerTypeLabel,
  cancerTypeLookup,
  productTypeId,
  setProductTypeId,
  selectedProductTypeLabel,
  colors,
  bottom,
}: DirectoryModFiltersProps) => {
  return (
    <>
      <DirectorySearchBox />

      <DirectoryCancerTypeFilter
        className="w-full min-w-0 flex-none md:w-80 md:shrink-0"
        focusId={focusId}
        setFocusId={setFocusId}
        selectedCancerTypeLabel={selectedCancerTypeLabel}
        cancerTypeLookup={cancerTypeLookup}
        colors={colors}
        bottom={bottom}
      />

      <DirectoryProductTypeFilter
        className="w-full min-w-0 flex-none md:w-80 md:shrink-0"
        productTypeId={productTypeId}
        setProductTypeId={setProductTypeId}
        selectedProductTypeLabel={selectedProductTypeLabel}
        colors={colors}
        bottom={bottom}
      />
    </>
  );
};
